import { useCustom } from "@refinedev/core";
import { AlertTriangle, PackageSearch } from "lucide-react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { query } from "@/lib/api";
import { boxWord, errorMessage, supplyWord, thingWord } from "@/lib/format";
import { pageData } from "@/lib/loaded";
import type { FbsBoxes, FbsSupply } from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

/** Сколько строк добавляет одно нажатие «показать ещё». Ровно страница API. */
const PAGE = 300;

/** Платформы раздела. Порядок — порядок вкладок на экране. */
const PLATFORMS = [
  { key: "ozon", name: "Ozon" },
  { key: "wb", name: "Wildberries" },
] as const;

/**
 * FBS Короба: что ещё числится за нами из уехавшего к маркетплейсу.
 *
 * Раздел «Короба» отвечает на «какой короб везти первым», «Товары» — на
 * «сколько их у нас и где». Здесь вопрос другой и неприятный: **вещь уже
 * уехала, а в учёте всё ещё наша.** Пока её не списали, склад считает своим
 * товар, которого на нём физически нет.
 *
 * Отсюда два числа в каждой строке, и путать их нельзя: «ждут списания» —
 * вещи, которые УЖЕ у маркетплейса, «у нас» — те, что действительно лежат в
 * коробе (отменённый заказ, невыехавшая поставка). Первое — работа для
 * автомата, второе — работа для рук.
 *
 * Платформа живёт в АДРЕСЕ, как и город: ссылку «короба Ozon» отправляют
 * сменщику, и открыться у него она обязана такой же.
 */
export function FbsBoxesPage() {
  const [params, setParams] = useSearchParams();
  const platform = params.get("platform") === "wb" ? "wb" : "ozon";
  const cityParam = params.get("city") ?? "";
  const limitParam = params.get("n") ?? "";

  /** Правит ОДИН ключ адреса, не трогая остальные. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Смена платформы или города возвращает список к первой странице:
    // поднятый потолок относился к прежнему отбору. Города у платформ тоже
    // разные — в Самаре короб ВБ есть, а озоновского нет вовсе.
    if (key === "platform") next.delete("city");
    if (key !== "n") next.delete("n");
    setParams(next, { replace: true });
  };

  const url = useMemo(
    () =>
      `/api/fbs/boxes${query({
        platform,
        city_id: cityParam || undefined,
        limit: limitParam || undefined,
      })}`,
    [platform, cityParam, limitParam],
  );

  const listQuery = useCustom<FbsBoxes>({ url, method: "get" });
  useSessionGuard(listQuery.query.error);

  // Списком считается только payload, в котором ДЕЙСТВИТЕЛЬНО есть строки:
  // `result.data` у Refine существует и до загрузки — пустым объектом, и
  // обращение к вложенным полям на нём роняет экран целиком (поймано на бою
  // 08.09.26, разбор — в `lib/loaded.ts`).
  const list = pageData(listQuery.result.data, "rows");
  const busy = listQuery.query.isFetching;
  const error = listQuery.query.error ? errorMessage(listQuery.query.error) : "";

  const rows = list?.rows ?? [];
  const cities = list?.cities ?? [];
  // «Ждут списания» известно не всегда: базы задач соседа у песочной полосы
  // нет вовсе. Тогда экран показывает короба и честно молчит про списание —
  // ноль читался бы как «всё разобрано».
  const known = list?.neighbour.units ?? false;
  const shipped = list?.totals.shipped ?? 0;
  const here = list?.totals.here ?? 0;

  return (
    <div
      className="flex h-full min-h-0 flex-col p-4"
      data-component="FbsBoxesPage"
      data-annot="fbs короба"
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[15px] font-semibold">FBS Короба</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            Короба отгрузки: что ещё числится за складом из уехавшего к
            маркетплейсу.
          </p>
        </div>

        <div data-annot="фильтр" className="flex flex-wrap items-center gap-2">
          {/* Платформа — не фильтр списка, а РАЗНЫЕ склады: своя группа мест,
              свой кабинет, свои документы отгрузки. Поэтому переключатель, а
              не строка выпадающего списка рядом с городом. */}
          <div
            data-annot="платформа"
            className="inline-flex rounded-[var(--radius-ui)] border border-border p-0.5"
          >
            {PLATFORMS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => set("platform", p.key)}
                aria-pressed={platform === p.key}
                className={
                  "h-7 rounded-[calc(var(--radius-ui)-2px)] px-3 text-[12.5px] " +
                  (platform === p.key
                    ? "bg-accent-dim font-medium text-accent-text"
                    : "text-fg-muted hover:text-fg")
                }
              >
                {p.name}
              </button>
            ))}
          </div>

          <Combo
            label="город"
            value={cityParam}
            onChange={(v) => set("city", v)}
            width={190}
            options={[
              { value: "", label: "все" },
              ...cities.map((c) => ({
                value: String(c.city_id),
                label: c.name,
                meta: String(c.boxes),
                title: `${c.name}: ${c.boxes} ${boxWord(c.boxes)}, ${c.units} шт`,
              })),
            ]}
          />
        </div>
      </div>

      {error ? (
        <div
          role="alert"
          className="mb-3 flex items-center gap-2 rounded-[var(--radius-ui)] border border-err/60 bg-err-bg px-3 py-2 text-[12.5px]"
        >
          <AlertTriangle size={14} className="text-err" />
          <span>{error}</span>
          <button
            type="button"
            onClick={() => listQuery.query.refetch()}
            className="ml-auto rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-[12px] hover:bg-bg-hover"
          >
            Повторить
          </button>
        </div>
      ) : null}

      {!list ? (
        <p className="text-[13px] text-fg-muted">Считаю…</p>
      ) : (
        <>
          <dl
            data-annot="сводка"
            className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4"
            aria-busy={busy}
          >
            <Metric
              label="Непустых коробов"
              value={list.total}
              unit={boxWord(list.total)}
              note={`из ${list.totals.boxes_total} в группе`}
              parts={cities.map((c) => ({
                key: c.city_id,
                name: c.name,
                units: c.boxes,
                active: cityParam === String(c.city_id),
                onClick: () => set("city", String(c.city_id)),
              }))}
            />
            {/* ГЛАВНОЕ ЧИСЛО РАЗДЕЛА: вещи, которых у нас физически нет, а в
                учёте они наши. Именно их разбирает автомат списания. */}
            <Metric
              label="Ждут списания"
              value={known ? shipped : "—"}
              unit={known ? thingWord(shipped) : ""}
              accent
              note={
                known
                  ? `уже у маркетплейса из ${list.totals.units} шт`
                  : "базы задач FBS нет в этой полосе"
              }
            />
            {/* Второе число той же строки, и работа за ним другая: эти вещи
                РЕАЛЬНО лежат в коробе — отменённый заказ, невыехавшая
                поставка. Их достают руками, а не списывают. */}
            <Metric
              label="Ещё у нас"
              value={known ? here : "—"}
              unit={known ? thingWord(here) : ""}
              note={known ? "лежат в коробах на самом деле" : "не посчитано"}
            />
            <Metric
              label="Отгрузок"
              value={list.totals.supplies}
              unit={supplyWord(list.totals.supplies)}
              note={
                list.neighbour.supply
                  ? "разных документов за коробами"
                  : "кабинет недоступен"
              }
            />
          </dl>

          {rows.length === 0 ? (
            <Empty>
              {cityParam
                ? "В этом городе нет ни одного непустого короба этой площадки."
                : "Все короба этой площадки разобраны — числиться нечему."}
            </Empty>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
              <table
                data-annot="таблица"
                className="w-full border-collapse text-[13px]"
              >
                <caption className="sr-only">
                  Короба отгрузки {list.platform_name}, сверху уехавшие раньше
                  всех
                </caption>
                <thead className="hidden md:table-header-group">
                  <tr>
                    {[
                      "Короб",
                      "Город",
                      "Штук",
                      "Ждут списания",
                      "У нас",
                      "Отгрузка",
                    ].map((title) => (
                      <th
                        key={title}
                        scope="col"
                        className={
                          "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg " +
                          (title === "Короб" ||
                          title === "Город" ||
                          title === "Отгрузка"
                            ? "text-left"
                            : "text-right")
                        }
                      >
                        {title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block md:table-row-group">
                  {rows.map((row) => (
                    <tr
                      key={row.place_id}
                      data-box={row.code}
                      // Адрес замечания — НОМЕР короба, а не позиция в списке:
                      // «строка 53838608» находится на складе, «третья
                      // сверху» — нет, порядок меняется со следующей отгрузкой.
                      data-annot={`строка ${row.code}`}
                      className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
                    >
                      <Cell label="Короб">
                        <span className="flex flex-col">
                          <Link
                            to={`/fbs/${row.place_id}`}
                            className="font-medium tnum text-fg hover:text-accent-text"
                          >
                            {row.name || row.code}
                          </Link>
                          {/* Код места — то, чем короб берут СКАНЕРОМ. */}
                          <span className="text-[11px] tnum text-fg-muted">
                            {row.place_barcode || "без кода места"}
                          </span>
                        </span>
                      </Cell>
                      <Cell label="Город">{row.city}</Cell>
                      <Cell label="Штук" align="text-right tnum">
                        {row.units}
                      </Cell>
                      <Cell label="Ждут списания" align="text-right tnum">
                        <Waiting value={row.shipped} />
                      </Cell>
                      <Cell label="У нас" align="text-right tnum">
                        <Here value={row.here} />
                      </Cell>
                      <Cell label="Отгрузка">
                        <SupplyCell supply={row.supply} />
                      </Cell>
                    </tr>
                  ))}
                </tbody>
              </table>
              {list.shown < list.total ? (
                <div className="border-t border-border p-3 text-center">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => set("n", String(list.shown + PAGE))}
                    className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-60"
                  >
                    {busy
                      ? "Гружу…"
                      : `Показать ещё ${Math.min(PAGE, list.total - list.shown)}`}
                  </button>
                  <p className="mt-1.5 text-[11.5px] text-fg-muted">
                    Показано {list.shown} из {list.total}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Сколько вещей короба ждут списания.
 *
 * Прочерк вместо нуля, когда базы задач нет: ноль читался бы как «всё
 * списано», а мы этого не знаем. Настоящий ноль — тихий: работа по коробу
 * сделана, и подсвечивать её незачем.
 */
function Waiting({ value }: { value: number | null }) {
  if (value === null) return <span className="text-fg-muted">—</span>;
  if (value === 0) return <span className="text-fg-muted">0</span>;
  return <span className="font-semibold text-accent-text">{value}</span>;
}

/** Сколько вещей ЛЕЖИТ в коробе на самом деле — их достают руками. */
function Here({ value }: { value: number | null }) {
  if (value === null) return <span className="text-fg-muted">—</span>;
  if (value === 0) return <span className="text-fg-muted">0</span>;
  return <span className="font-medium text-warn">{value}</span>;
}

/**
 * Документ отгрузки: чем короб уехал и когда.
 *
 * Номер и дата стоят вместе, потому что порознь бесполезны: по номеру короб
 * ищут в кабинете маркетплейса, по дате понимают, сколько он уже висит
 * несписанным.
 */
export function SupplyCell({ supply }: { supply: FbsSupply | null }) {
  if (!supply)
    return <span className="text-[12px] text-fg-muted">не в отгрузке</span>;
  return (
    <span className="flex flex-col">
      <span className="tnum text-[12.5px]">{supply.id}</span>
      <span className="text-[11px] text-fg-muted">
        {supply.label}
        {supply.date ? ` · ${supply.date}` : ""}
      </span>
    </span>
  );
}

/**
 * Ячейка таблицы, которая на узком экране становится строкой «подпись —
 * значение». Тот же приём, что в остальных списках кабинета: второго набора
 * разметки под мобильный экран не появляется, расходиться им негде.
 */
export function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      data-annot={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}

/** Плитка сводки. `parts` — разложение главного числа на слагаемые. */
function Metric({
  label,
  value,
  unit,
  note,
  accent,
  parts,
}: {
  label: string;
  value: string | number;
  unit: string;
  note: string;
  accent?: boolean;
  parts?: {
    key: number;
    name: string;
    units: number;
    active: boolean;
    onClick: () => void;
  }[];
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3">
      <dt className="text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "mt-1 text-[24px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
        <span className="ml-1 text-[13px] font-normal text-fg-muted">
          {unit}
        </span>
      </dd>
      {parts && parts.length > 0 ? (
        <div data-annot="города" className="mt-1.5 flex flex-wrap gap-1">
          {parts.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={p.onClick}
              title={`Только короба города ${p.name}`}
              className={
                "rounded border px-1.5 py-px text-[12px] " +
                (p.active
                  ? "border-accent/60 bg-accent-dim text-accent-text"
                  : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
              }
            >
              {p.name}{" "}
              <span className="tnum font-medium text-fg">{p.units}</span>
            </button>
          ))}
        </div>
      ) : null}
      {note ? <p className="mt-1 text-[11.5px] text-fg-muted">{note}</p> : null}
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
      <PackageSearch size={20} className="mx-auto mb-2 text-fg-muted" />
      {children}
    </div>
  );
}
